import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { setTokenGetter, setUnauthorizedHandler } from '../utils/api';
import api from '../utils/api';
import useFlowStore from '../stores/useFlowStore';
import axios from 'axios';
import toast from 'react-hot-toast';
import { logger } from '../utils/logger';

const AuthContext = createContext(null);

// Detects Auth0 errors that mean the session is truly expired and the user
// must authenticate again (refresh token missing, revoked, or expired).
function isSessionExpiredError(error) {
  if (!error) return false;
  const code = (error.error || '').toLowerCase();
  const msg  = (error.message || '').toLowerCase();
  return (
    code === 'login_required'       ||
    code === 'invalid_grant'        ||
    code === 'consent_required'     ||
    code === 'interaction_required' ||
    msg.includes('missing refresh token') ||
    msg.includes('refresh token')   ||
    msg.includes('login_required')
  );
}

// Helper function to decode JWT without verification (frontend only)
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    logger.error('Error decoding JWT:', error);
    return null;
  }
}

export const AuthProvider = ({ children }) => {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
    error: auth0Error
  } = useAuth0();

  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const loginTracked = useRef(false);
  const loginTime = useRef(null);
  // Prevents multiple simultaneous relogin redirects when the session expires
  const reloginPending = useRef(false);

  // Get token and setup token getter for API calls
  useEffect(() => {
    const setupToken = async () => {
      if (isAuthenticated && auth0User) {
        try {
          const accessToken = await getAccessTokenSilently();
          setToken(accessToken);
          
          // Setup token getter for axios — with timeout to avoid hanging API calls
          // if Auth0 silent refresh (iframe) fails (e.g. Allowed Web Origins not set)
          setTokenGetter(async () => {
            try {
              const tokenPromise = getAccessTokenSilently();
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('getAccessTokenSilently timeout')), 5000)
              );
              return await Promise.race([tokenPromise, timeoutPromise]);
            } catch (error) {
              logger.error('[Auth] Error getting token in getter:', error.message);
              if (isSessionExpiredError(error) && !reloginPending.current) {
                reloginPending.current = true;
                logger.warn('[Auth] Session expired in token getter — redirecting to login');
                toast.error('Your session has expired. Please log in again.');
                loginWithRedirect({ appState: { returnTo: window.location.pathname } });
                return null;
              }
              // Return the last known token as fallback rather than null,
              // so in-flight requests still carry an auth header
              return token || null;
            }
          });

          // Register 401 handler so backend auth failures also trigger relogin
          setUnauthorizedHandler(() => {
            if (!reloginPending.current) {
              reloginPending.current = true;
              logger.warn('[Auth] 401 received from API — redirecting to login');
              toast.error('Your session has expired. Please log in again.');
              loginWithRedirect({ appState: { returnTo: window.location.pathname } });
            }
          });

          logger.log('[Auth] Auth0 token obtained');
          logger.log('[Auth] User:', auth0User);
        } catch (error) {
          logger.error('[Auth] Error getting token:', error);
          if (isSessionExpiredError(error) && !reloginPending.current) {
            reloginPending.current = true;
            logger.warn('[Auth] Session expired on setup — redirecting to login');
            toast.error('Your session has expired. Please log in again.');
            await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
          }
        }
      }
    };

    setupToken();

    // Refresh token periodically
    if (isAuthenticated) {
      const interval = setInterval(async () => {
        try {
          const accessToken = await getAccessTokenSilently({
            ignoreCache: true
          });
          setToken(accessToken);
          logger.log('[Auth] Token refreshed');
        } catch (error) {
          logger.error('[Auth] Error refreshing token:', error);
          if (isSessionExpiredError(error) && !reloginPending.current) {
            reloginPending.current = true;
            clearInterval(interval);
            logger.warn('[Auth] Session expired during refresh — redirecting to login');
            toast.error('Your session has expired. Please log in again.');
            loginWithRedirect({ appState: { returnTo: window.location.pathname } });
          }
        }
      }, 5 * 60 * 1000); // Refresh every 5 minutes

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, auth0User, getAccessTokenSilently]);

  // Extract user info from Auth0 user object, token, and Management API profile
  useEffect(() => {
    if (!isAuthenticated || !auth0User || !token) {
      setUser(null);
      loginTracked.current = false;
      return;
    }

    const buildUserInfo = async () => {
      // Decode the access token to extract roles
      const decodedToken = decodeJWT(token);

      const roles = decodedToken?.['https://schemagraphos.com/roles'] ||
                    decodedToken?.['roles'] ||
                    decodedToken?.['permissions'] ||
                    auth0User['https://schemagraphos.com/roles'] ||
                    auth0User['roles'] ||
                    [];

      // 1st attempt: read expire_date from token claims (requires updated Action)
      let expireDate =
        auth0User['https://schemagraphos.com/expire_date'] ||
        decodedToken?.['https://schemagraphos.com/expire_date'] ||
        null;

      // 2nd attempt (fallback): read directly from Auth0 user_metadata via backend
      if (!expireDate) {
        try {
          const profileRes = await api.get('/user/profile');
          const meta = profileRes?.data?.user_metadata || {};
          expireDate = meta.expire_date || null;
          logger.log('[Auth] expire_date from Management API:', expireDate);
        } catch (err) {
          logger.warn('[Auth] Could not fetch user profile from backend:', err.message);
        }
      }

      const userInfo = {
        id: auth0User.sub,
        email: auth0User.email || '',
        username: auth0User.email?.split('@')[0] || '',
        name: auth0User.name || auth0User.email || '',
        firstName: auth0User.given_name || '',
        lastName: auth0User.family_name || '',
        picture: auth0User.picture || '',
        roles: Array.isArray(roles) ? roles : (roles ? [roles] : []),
        isAdmin: Array.isArray(roles) ? roles.includes('ADMIN') : roles === 'ADMIN',
        expireDate,
      };

      setUser(userInfo);
      logger.log('[Auth] User info set:', userInfo);

      if (!loginTracked.current) {
        trackLogin(token, userInfo);
        loginTracked.current = true;
        loginTime.current = Date.now();
      }
    };

    buildUserInfo();
  }, [isAuthenticated, auth0User, token]);

  // Handle Auth0 errors
  useEffect(() => {
    if (auth0Error) {
      logger.error('[Auth] Auth0 error:', auth0Error);
    }
  }, [auth0Error]);

  // ── Session timeout management ───────────────────────────────────────────
  //
  // Uses localStorage so timers survive page reloads / browser restarts.
  //
  // Keys:
  //   sg_login_time    — absolute timestamp of when this session started
  //   sg_last_activity — last time the user interacted with the page
  //
  // Rules:
  //   • Max session : 24 h from login (hard limit, ignores activity)
  //   • Inactivity  :  1 h without any user interaction
  //
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const MAX_SESSION_MS = 24 * 60 * 60 * 1000; // 24 h
    const INACTIVITY_MS  =  1 * 60 * 60 * 1000; //  1 h
    const ACTIVITY_EVENTS = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];
    const LS_LOGIN    = 'sg_login_time';
    const LS_ACTIVITY = 'sg_last_activity';

    const now = Date.now();

    // Record login time once per session (persists across page reloads)
    if (!localStorage.getItem(LS_LOGIN)) {
      localStorage.setItem(LS_LOGIN, now.toString());
    }
    if (!localStorage.getItem(LS_ACTIVITY)) {
      localStorage.setItem(LS_ACTIVITY, now.toString());
    }

    const loginTs    = parseInt(localStorage.getItem(LS_LOGIN),    10);
    const lastActTs  = parseInt(localStorage.getItem(LS_ACTIVITY), 10);

    const forceLogout = (reason) => {
      localStorage.removeItem(LS_LOGIN);
      localStorage.removeItem(LS_ACTIVITY);
      if (reason === 'max_duration') {
        toast.error('Your 24-hour session has expired. Please log in again.');
      } else {
        toast.error('Session expired due to inactivity. Please log in again.');
      }
      logout(reason);
    };

    // ── Check immediately on (re)load ─────────────────────────────────────
    if (now - loginTs >= MAX_SESSION_MS) {
      forceLogout('max_duration');
      return;
    }
    if (now - lastActTs >= INACTIVITY_MS) {
      forceLogout('inactivity');
      return;
    }

    // ── Schedule absolute 24-hour logout ─────────────────────────────────
    // Calculates the *remaining* time so a reload mid-session schedules
    // the logout at exactly 24 h from the original login, not from reload.
    const maxRemaining = MAX_SESSION_MS - (now - loginTs);
    const maxTimer = setTimeout(() => forceLogout('max_duration'), maxRemaining);

    // ── Inactivity: poll every minute, compare against localStorage ───────
    // This survives reloads: on next load the check above fires immediately
    // if the user was away for more than 1 h.
    const inactivityInterval = setInterval(() => {
      const last = parseInt(localStorage.getItem(LS_ACTIVITY) || '0', 10);
      if (Date.now() - last >= INACTIVITY_MS) {
        clearInterval(inactivityInterval);
        clearTimeout(maxTimer);
        forceLogout('inactivity');
      }
    }, 60_000); // check every minute

    // ── Track activity (throttled — at most one localStorage write / 30 s) ─
    let activityThrottle = null;
    const handleActivity = () => {
      if (!activityThrottle) {
        activityThrottle = setTimeout(() => {
          localStorage.setItem(LS_ACTIVITY, Date.now().toString());
          activityThrottle = null;
        }, 30_000);
      }
    };

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, handleActivity));

    logger.log(`[Session] Started. Max remaining: ${Math.round(maxRemaining / 60000)} min`);

    return () => {
      clearTimeout(maxTimer);
      clearInterval(inactivityInterval);
      if (activityThrottle) clearTimeout(activityThrottle);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, handleActivity));
    };
  }, [isAuthenticated, user]); // re-runs only when auth state changes, NOT on token refresh

  // Track login event in backend
  const trackLogin = async (accessToken, userInfo) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      
      // Extract provider from user ID (e.g., "google-oauth2|123" -> "Google")
      let provider = 'Unknown';
      if (userInfo.id.includes('google')) provider = 'Google';
      else if (userInfo.id.includes('facebook')) provider = 'Facebook';
      else if (userInfo.id.includes('microsoft')) provider = 'Microsoft';
      else if (userInfo.id.includes('apple')) provider = 'Apple';
      else if (userInfo.id.includes('auth0')) provider = 'Auth0';
      
      await axios.post(
        `${API_URL}/api/user/track-login`,
        {
          provider,
          device: navigator.userAgent,
          browser: navigator.userAgent.match(/(Firefox|Chrome|Safari|Opera|Edge)/)?.[0] || 'Unknown'
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      logger.log('[Tracking] Login tracked in backend');
    } catch (error) {
      // Don't fail if tracking fails
      logger.error('[Tracking] Failed to track login:', error.message);
    }
  };

  // Track logout event in backend
  const trackLogout = async (accessToken, reason = 'manual') => {
    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
      
      // Calculate session duration
      const sessionDuration = loginTime.current 
        ? Math.round((Date.now() - loginTime.current) / 1000) // in seconds
        : 0;
      
      await axios.post(
        `${API_URL}/api/user/track-logout`,
        {
          sessionDuration,
          reason
        },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      logger.log(`[Tracking] Logout tracked in backend (reason: ${reason})`);
    } catch (error) {
      // Don't fail if tracking fails
      logger.error('[Tracking] Failed to track logout:', error.message);
    }
  };

  const login = async () => {
    try {
      logger.log('[Auth] Initiating login...');
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname }
      });
    } catch (error) {
      logger.error('[Auth] Login error:', error);
      throw error;
    }
  };

  const logout = async (reason = 'manual') => {
    logger.log(`[Auth] Logging out... (reason: ${reason})`);

    // Reset flow store state so no model data leaks across user sessions
    try {
      useFlowStore.getState().resetFlow();
    } catch (e) {}

    // Always clear persisted session keys
    localStorage.removeItem('sg_login_time');
    localStorage.removeItem('sg_last_activity');

    // Track logout before actually logging out
    if (token) {
      await trackLogout(token, reason);
    }

    // Reset tracking refs
    loginTracked.current = false;
    loginTime.current = null;

    auth0Logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  const hasAnyRole = (...roles) => {
    return roles.some(role => hasRole(role));
  };

  // License is expired when expireDate exists and is in the past
  const licenseExpired = (() => {
    if (!user?.expireDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(user.expireDate + 'T00:00:00') < today;
  })();

  // User can save only if they have a role AND their license is not expired
  const userHasRole = user?.roles?.length > 0 && !licenseExpired;

  const value = {
    authenticated: isAuthenticated,
    loading: isLoading,
    user,
    token,
    login,
    logout,
    hasRole,
    hasAnyRole,
    userHasRole,
    licenseExpired,
    isAdmin: user?.isAdmin || false,
    getAccessToken: getAccessTokenSilently,
    error: auth0Error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
